import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { keyframes } from "@mui/system";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import SecurityIcon from "@mui/icons-material/Security";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CareXLogo from "../components/CareXLogo";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { useNavigate } from "react-router-dom";
import ForgotPasswordPage from "./ForgotPasswordPage";

// ─── Animations ──────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50%       { opacity: 0.7; transform: scale(1.05); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ─── Stat Widget ─────────────────────────────────────────────────────────────
const StatBadge: React.FC<{ value: string; label: string; delay: string }> = ({
  value,
  label,
  delay,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      px: 3,
      py: 1.5,
      borderRadius: "14px",
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.15)",
      backdropFilter: "blur(10px)",
      animation: `${fadeUp} 0.6s ease-out ${delay} both`,
    }}
  >
    <Typography
      sx={{
        fontSize: "1.5rem",
        fontWeight: 700,
        color: "#FFFFFF",
        lineHeight: 1,
        mb: 0.25,
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.65)", fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

// ─── Feature Item ─────────────────────────────────────────────────────────────
const FeatureItem: React.FC<{
  icon: React.ReactNode;
  title: string;
  desc: string;
  delay: string;
}> = ({ icon, title, desc, delay }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      animation: `${fadeUp} 0.6s ease-out both`,
      animationDelay: delay,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "12px",
        background: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        backdropFilter: "blur(8px)",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 600, color: "#FFFFFF", fontSize: "0.95rem", mb: 0.25 }}>
        {title}
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", lineHeight: 1.5 }}>
        {desc}
      </Typography>
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const LoginPage: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [selectedTenant, setSelectedTenant] = useState("Lakewooodvilla");
  const [error, setError] = useState("");
  const [loginTab, setLoginTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const { setTenant, setCurrentTenantId } = useTenant();
  const navigate = useNavigate();
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user.role === "SUPER_ADMIN" || user.role === "ADMIN") {
          navigate("/dashboard");
        } else {
          navigate("/residents");
        }
      } else {
        navigate("/residents");
      }
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const success = await login(username, password);
      if (success) {
        if (loginTab === 0) {
          // Tenant selection logic removed as it relied on mock data
        }
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const user = JSON.parse(storedUser);
          if (user.role === "SUPER_ADMIN" || user.role === "ADMIN") {
            navigate("/dashboard");
          } else {
            navigate("/residents");
          }
        } else {
          navigate("/residents");
        }
      } else {
        setError("Invalid username or password");
      }
    } catch (err: any) {
      const msg: string = err.message || "An error occurred during login";
      if (msg === "Your account has been locked. Please contact your Administrator.") {
        setLockDialogOpen(true);
        setError("");
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordReset = (emailOrUsername: string) => {
    console.log("Resetting password for:", emailOrUsername);
    setOpenForgotPassword(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row", lg: "row" },
        alignItems: "stretch",
        fontFamily: "'Inter'",
        overflow: "hidden",
      }}
    >
      {/* ── Left Panel (Hero/Brand) ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          width: { md: "42%", lg: "52%" },
          flexDirection: "column",
          justifyContent: "space-between",
          p: { md: 4, lg: 7 },
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(145deg, #072A5E 0%, #0A3B82 35%, #0C4EA8 70%, #1565C0 100%)",
        }}
      >
        {/* Decorative blobs */}
        <Box
          sx={{
            position: "absolute",
            top: "-20%",
            right: "-15%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(100,181,246,0.15) 0%, transparent 70%)",
            animation: `${pulse} 6s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-10%",
            left: "-10%",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(41,182,246,0.12) 0%, transparent 70%)",
            animation: `${pulse} 8s ease-in-out infinite 1s`,
          }}
        />
        {/* Grid pattern overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='0.03'%3E%3Cpath d='M0 0h40v1H0V0zm0 39h40v1H0v-1zM0 0v40H1V0H0zm39 0v40h1V0h-1z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />

        {/* Content */}
        <Box sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          flex: { md: 1, lg: "unset" },
          justifyContent: { md: "center", lg: "flex-start" },
          pb: { md: 4, lg: 0 }
        }}>
          {/* Logo */}
          <Box sx={{ mb: { md: 10, lg: 8 }, animation: `${fadeUp} 0.5s ease-out` }}>
            <Box sx={{
              backgroundColor: '#FFFFFF',
              display: 'inline-flex',
              p: 1.5,
              px: 2.5,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <CareXLogo style={{ height: "auto", width: "150px" }} />
            </Box>
          </Box>

          {/* Headline */}
          <Box sx={{ animation: `${fadeUp} 0.6s ease-out 0.1s both` }}>
            <Chip
              label="Healthcare Management Platform"
              size="small"
              sx={{
                mb: 3,
                backgroundColor: "rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.85)",
                border: "1px solid rgba(255,255,255,0.2)",
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#FFFFFF",
                mb: 2,
                lineHeight: 1.15,
                fontSize: { md: "1.7rem", lg: "2.6rem" },
                letterSpacing: "-0.03em",
              }}
            >
              Clinical Excellence,{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(90deg, #64B5F6, #80DEEA)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Simplified
              </Box>
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.65)",
                fontSize: "1rem",
                lineHeight: 1.7,
                maxWidth: 420,
              }}
            >
              A unified platform for care teams, from clinical notes to
              medication management, all in one secure workspace.
            </Typography>
          </Box>

          {/* Features */}
          <Box sx={{ mt: { md: 3, lg: 5 }, display: "flex", flexDirection: "column", gap: { md: 2, lg: 3 } }}>
            <FeatureItem
              icon={<HealthAndSafetyIcon sx={{ fontSize: 22, color: "#80DEEA" }} />}
              title="Patient-Centered Care"
              desc="Streamlined clinical notes, vitals tracking, and medication management."
              delay="0.2s"
            />
            <FeatureItem
              icon={<SecurityIcon sx={{ fontSize: 22, color: "#A5D6A7" }} />}
              title="Secure & Compliant"
              desc="Secure data handling with role-based access protocols."
              delay="0.3s"
            />
            <FeatureItem
              icon={<AssignmentTurnedInIcon sx={{ fontSize: 22, color: "#FFE082" }} />}
              title="Workflow Optimization"
              desc="Intuitive task management and automated census reporting."
              delay="0.4s"
            />
          </Box>
        </Box>

        {/* Stats bar at bottom */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            gap: 2,
            animation: `${fadeUp} 0.7s ease-out 0.5s both`,
          }}
        >
          <StatBadge value="99.9%" label="Uptime SLA" delay="0.5s" />
          {/* <StatBadge value="HIPAA" label="Compliant" delay="0.6s" /> */}
          <StatBadge value="256-bit" label="Encryption" delay="0.7s" />
        </Box>
      </Box>

      {/* ── Right Panel (Login Form) ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, sm: 5, md: 6, lg: 8 },
          background: "#F4F7FC",
          position: "relative",
          overflowY: "auto",
        }}
      >
        {/* Subtle top line */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: "linear-gradient(90deg, #0A3B82 0%, #1976D2 50%, #29B6F6 100%)",
          }}
        />

        <Box
          sx={{
            width: "100%",
            maxWidth: 420,
            animation: `${fadeUp} 0.6s ease-out 0.15s both`,
          }}
        >
          {/* Mobile logo — visible only on xs/sm */}
          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 5 }}>
            <CareXLogo style={{ height: "auto", width: "130px" }} />
          </Box>

          {/* Form header */}
          <Box sx={{ mb: 5 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "16px",
                background: "linear-gradient(135deg, #0A3B82 0%, #1565C0 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                boxShadow: "0 8px 24px rgba(10, 59, 130, 0.28)",
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 26, color: "#FFFFFF" }} />
            </Box>
            <Typography
              sx={{
                fontSize: "1.65rem",
                fontWeight: 800,
                color: "#0D1B2E",
                mb: 0.75,
                letterSpacing: "-0.025em",
              }}
            >
              Welcome To CareX
            </Typography>
            <Typography sx={{ color: "#64748B", fontSize: "0.92rem", lineHeight: 1.6 }}>
              Sign in to your CareX facility portal to continue.
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              icon={false}
              sx={{
                mb: 3,
                borderRadius: "10px",
                fontSize: "0.85rem",
                animation: `${fadeIn} 0.3s ease-out`,
                backgroundColor: "#FEF2F2",
                color: "#991B1B",
                border: "1px solid #FCA5A5",
                py: 1,
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Username */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151", mb: 0.75 }}
              >
                Email
              </Typography>
              <TextField
                required
                fullWidth
                id="username"
                name="username"
                autoComplete="username"
                autoFocus
                placeholder="e.g. jdoe@facility.com"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    "& fieldset": { borderColor: "#E5E7EB", borderWidth: "1.5px" },
                    "&:hover fieldset": { borderColor: "#1565C0" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0A3B82",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    py: "13px",
                    fontSize: "0.93rem",
                    color: "#111827",
                    "&::placeholder": { color: "#9CA3AF", opacity: 1 },
                  },
                }}
              />
            </Box>

            {/* Password */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ mb: 0.75 }}>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151" }}>
                  Password
                </Typography>
              </Box>
              <TextField
                required
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                placeholder="Enter your secure password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                        sx={{ color: "#9CA3AF", "&:hover": { color: "#1565C0" } }}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                        ) : (
                          <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    "& fieldset": { borderColor: "#E5E7EB", borderWidth: "1.5px" },
                    "&:hover fieldset": { borderColor: "#1565C0" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0A3B82",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    py: "13px",
                    fontSize: "0.93rem",
                    color: "#111827",
                    "&::placeholder": { color: "#9CA3AF", opacity: 1 },
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => setOpenForgotPassword(true)}
                  sx={{
                    fontSize: "0.82rem",
                    color: "#1565C0",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline", color: "#0A3B82" },
                  }}
                >
                  Forgot password?
                </Link>
              </Box>
            </Box>

            {/* Sign In Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              endIcon={!isLoading && <ArrowForwardIcon />}
              disableElevation
              sx={{
                mt: 4,
                py: "13px",
                fontWeight: 700,
                fontSize: "0.95rem",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #0A3B82 0%, #1565C0 100%)",
                textTransform: "none",
                letterSpacing: "0.01em",
                boxShadow: "0 4px 16px rgba(10,59,130,0.35)",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #083070 0%, #0D5099 100%)",
                  boxShadow: "0 6px 22px rgba(10,59,130,0.45)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: isLoading ? "none" : "translateY(0)",
                },
                "&.Mui-disabled": {
                  background: "linear-gradient(135deg, #0A3B82 0%, #1565C0 100%)",
                  opacity: 0.65,
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Sign In"
              )}
            </Button>

            {/* Trust indicators */}
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 14, color: "#6B7280" }} />
              <Typography sx={{ color: "#9CA3AF", fontSize: "0.73rem", fontWeight: 500 }}>
                Authorized Personnel Only · 256-bit SSL
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Forgot Password Dialog */}
      <ForgotPasswordPage
        open={openForgotPassword}
        onClose={() => setOpenForgotPassword(false)}
        onReset={handleForgotPasswordReset}
      />

      {/* Account Locked Dialog */}
      <Dialog
        open={lockDialogOpen}
        onClose={() => setLockDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            p: 3,
            maxWidth: 400,
            textAlign: "center",
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
            backgroundColor: "#FFFFFF",
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#FEE2E2", // Light red background
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 32, color: "#DC2626" }} />
          </Box>
        </Box>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "20px", color: "#111827", p: 0, mb: 2 }}>
          Account Temporarily Locked
        </DialogTitle>
        <DialogContent sx={{ p: 0, mb: 3 }}>
          <DialogContentText sx={{ color: "#4B5563", fontSize: "0.95rem", lineHeight: 1.5, textAlign: "center" }}>
            Your Account has been Temporarily Locked Due To Multiple Unsuccessful Sign-In Attempts.
            <br /><br />
            Please Contact Your Administrator To Unlock Your Account.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: 0 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setLockDialogOpen(false)}
            sx={{
              py: 1.2,
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
              backgroundColor: "#DC2626",
              color: "#FFFFFF",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPage;